import * as React from 'react';
import {
  useInteractions,
  type UseFloatingReturn,
  type UseListNavigationProps,
} from '@floating-ui/react';

import {TextInputProps} from '../TextInput';
import {useSelectionMode, useOptionListNavigation} from '../../hooks';

import type {SearchableListProps} from './_types';

export type SearchableListContextType = {
  context: UseFloatingReturn['context'];
  floatingStyles: UseFloatingReturn['floatingStyles'];
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
  handleInputChange?: TextInputProps['onChange'];
  inputValue: string;
  isLoading: boolean;
  isOpen: boolean;
  listRef: UseListNavigationProps['listRef'];
  options: SearchableListProps['options'];
  refs: UseFloatingReturn['refs'];
  renderEmptyStateLabel: SearchableListProps['renderEmptyStateLabel'];
  renderOption: SearchableListProps['renderOption'];
};

export const SearchableListContext =
  React.createContext<SearchableListContextType | null>(null);

export const useSearchableListContext = (): SearchableListContextType => {
  const context = React.useContext(SearchableListContext);
  const isTestEnvironment = process.env.NODE_ENV === 'test';

  if (!context && !isTestEnvironment) {
    throw new Error('You must use component within SearchableListContext');
  }

  /**
   * Context should only be null in a test environment.
   * We're okay with this because we want to be able to test SearchableList's subcomponents in isolation.
   */
  // @ts-expect-error TS2322 "null is not assignable to type SearchableListContextType"
  return context;
};

export type SearchableListIndexContextType = {
  activeIndex: UseListNavigationProps['activeIndex'];
  disabledIndices: ReturnType<
    typeof useOptionListNavigation
  >['disabledIndices'];
  getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
  listRef: UseListNavigationProps['listRef'];
  getIndexByValue: ReturnType<
    typeof useOptionListNavigation
  >['getIndexByValue'];
  flattenedOptions: ReturnType<
    typeof useOptionListNavigation
  >['flattenedOptions'];
  setActiveIndex: UseListNavigationProps['onNavigate'];
};

export const SearchableListIndexContext =
  React.createContext<SearchableListIndexContextType | null>(null);

export const useSearchableListIndexContext =
  (): SearchableListIndexContextType => {
    const context = React.useContext(SearchableListIndexContext);
    const isTestEnvironment = process.env.NODE_ENV === 'test';

    if (!context && !isTestEnvironment) {
      throw new Error(
        'You must use component within SearchableListSelectContext'
      );
    }

    /**
     * Context should only be null in a test environment.
     * We're okay with this because we want to be able to test SearchableList's subcomponents in isolation.
     */
    // @ts-expect-error TS2322 "null is not assignable to type SearchableListSelectContextType"
    return context;
  };

type SearchableListSelectionContextType = {
  checkmarkStyle: SearchableListProps['checkmarkStyle'];
  hasMaxSelections: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  isDisabled: boolean;
  getIsSelected: (value: string) => boolean;
  selectedOptions: ReturnType<typeof useSelectionMode>['selectedOptions'];
  onCreateOption: SearchableListProps['_onCreateOption'];
  selectionMode: SearchableListProps['selectionMode'];
  toggleSelectedValue: ReturnType<
    typeof useSelectionMode
  >['toggleSelectedValue'];
};

export const SearchableListSelectionContext =
  React.createContext<SearchableListSelectionContextType | null>(null);

export const useSearchableListSelectionContext =
  (): SearchableListSelectionContextType => {
    const context = React.useContext(SearchableListSelectionContext);
    const isTestEnvironment = process.env.NODE_ENV === 'test';

    if (!context && !isTestEnvironment) {
      throw new Error(
        'You must use component within SearchableListSelectContext'
      );
    }

    /**
     * Context should only be null in a test environment.
     * We're okay with this because we want to be able to test SearchableList's subcomponents in isolation.
     */
    // @ts-expect-error TS2322 "null is not assignable to type SearchableListSelectContextType"
    return context;
  };
